<script lang="ts" setup>
import TimeController from '@/common/components/molecules/timeController/TimeController.vue';
import UnitContainer from '@/common/components/molecules/unitContainer/UnitContainer.vue';
import {
  CHART_COLOR,
  setup,
  AlertHistoryTabProps,
} from '@/alert/components/alertDetail/alertHistory.setup';
import { AlertDetailEmit } from '@/alert/components/alertDetail/alertDetail.types';
import TextViewerWindow from '@/common/components/organisms/textViewerWindow/TextViewerWindow.vue';
import NotificationStatusWindow from '@/alert/components/notificationStatusWindow/NotificationStatusWindow.vue';
import ListHeader from '@/common/components/organisms/listHeader/ListHeader.vue';
import HelpTooltip from '@/common/components/molecules/HelpTooltip/HelpTooltip.vue';
import AlertTreeGrid from '@/alert/components/alertGrid/AlertTreeGrid.vue';
import AlertGrid from '@/alert/components/alertGrid/AlertGrid.vue';
import { USER_ENV_KEYS } from '@/common/define/userEnv.define';
import { MODE } from '@/common/components/molecules/timeController';
import NoDataTableBody from '@/common/components/molecules/noData/NoDataTableBody.vue';

const props = defineProps<AlertHistoryTabProps>();
const emit = defineEmits<AlertDetailEmit>();

const {
  periodInfo,
  onUpdatedIndicator,

  ruleInfo,

  chartOption,
  chartData,

  tabPanels,
  selectedTab,
  gridColumnType,
  gridColumns,
  gridData,
  isTreeMode,
  isShowMessageWindow,
  eventAlertInfo,
  selectedLabel,
  chartRef,
  onClickCell,
  onChartClick,
  t,
  barClickFromTime,
  barClickToTime,
  onclickExcelExport,
  notificationStatusWindowInfo,
  notificationStatusWindowTimePeriodInfo,
  onClickStatusButton,

  isSelectedChartLabel,
  isGridLoading,
  isFirstTabLoad,
} = setup(props, emit);

const { CRITICAL, WARNING, NORMAL } = CHART_COLOR;
</script>

<template>
  <section class="alert-detail-history">
    <!-- 탭 최초 진입/재진입/룰 변경 시 그리드 데이터 도착까지 표시되는 탭 영역 전체 로딩 -->
    <ev-loading :model-value="isFirstTabLoad" />
    <div class="alert-history-toolbar">
      <time-controller
        :mode="MODE.CURRENT_PERIOD_CALENDAR"
        :period-info="periodInfo"
        :options="{
          interval: 'I5s',
        }"
        @update-period-info="onUpdatedIndicator"
      />
    </div>
    <div class="alert-history-chart">
      <p class="alert-history-chart__title">{{ t('WORD.HISTORY') }}</p>
      <unit-container class="alert-history-chart__content">
        <ev-chart
          ref="chartRef"
          :selected-label="selectedLabel"
          :data="chartData"
          :options="chartOption"
          @click="onChartClick"
        />
        <ul class="chart-legend">
          <li class="chart-legend-item critical">Critical</li>
          <li class="chart-legend-item warning">Warning</li>
          <li class="chart-legend-item normal">Normal</li>
        </ul>
      </unit-container>
    </div>
    <ev-tabs
      v-model="selectedTab"
      :panels="tabPanels"
      stretch
      class="alert-history-tab"
    >
      <ev-tab-panel
        v-for="item in tabPanels"
        :key="item.value"
        :text="item.text"
        :value="item.value"
      >
        <ev-loading :model-value="isGridLoading" />
        <template v-if="selectedTab === 'sortByTime'">
          <list-header :title="t('WORD.HISTORY_LIST')">
            <template #titleInformation>
              <help-tooltip
                display-event-type="hover"
                pos="bottom"
              >
                <div class="rule-helps">
                  <div class="rule-help">
                    <h5 class="rule-help__header">{{ t('MESSAGE.ALERT_HISTORY_TITLE') }}</h5>
                    <p class="rule-help__content">
                      {{ t('MESSAGE.ALERT_HISTORY_MAX_ROWS') }}
                    </p>
                  </div>
                  <div class="rule-help">
                    <h5 class="rule-help__header">{{ t('WORD.ALERT') }}</h5>
                    <p class="rule-help__content">
                      <span>
                        {{ t('MESSAGE.CHECK_BY_TARGET_LAST_ALERT_HELP') }}
                      </span>
                      <span>
                        {{ t('MESSAGE.CHECK_BY_COMBINATION_LAST_ALERT_HELP') }}
                      </span>
                    </p>
                  </div>
                </div>
              </help-tooltip>
            </template>
          </list-header>
          <component
            :is="isTreeMode ? AlertTreeGrid : AlertGrid"
            :grid-columns="gridColumns"
            :grid-data="isSelectedChartLabel ? gridData : []"
            :rule-id="ruleInfo.alertRuleId"
            :options="{
              useExcelFromServer: true,
              useDashboardJump: true,
              gridOption: { emptyText: '' },
            }"
            :env-key="`${USER_ENV_KEYS.GRIDCOLUMNS_alert_alertDetail_historyList}_${gridColumnType}`"
            :filter-env-key="`${USER_ENV_KEYS.FILTERSEARCH_alert_alertDetail_historyList}_${gridColumnType}`"
            :export-excel-title="t('WORD.HISTORY_LIST')"
            type="detail"
            class="alert-history no-data-alert-history-table-body"
            :time-period-info="{
              fromTime: barClickFromTime,
              toTime: barClickToTime,
            }"
            @on-click-cell="onClickCell"
            @export-to-excel-from-server="onclickExcelExport"
            @click-status-button="onClickStatusButton"
          />
          <no-data-table-body
            target-class="no-data-alert-history-table-body"
            :is-visible="!isSelectedChartLabel || gridData.length === 0"
          >
            <td class="empty-table-body">
              <span v-if="!isSelectedChartLabel">{{
                t('MESSAGE.ALERT_HISTORY_NO_DATA_GUIDE')
              }}</span>
              <span v-else>{{ t('MESSAGE.NO_DATA') }}</span>
            </td>
          </no-data-table-body>
        </template>
      </ev-tab-panel>
    </ev-tabs>
  </section>
  <text-viewer-window
    v-model:is-show="isShowMessageWindow"
    :text="eventAlertInfo.messageText"
    is-network-alert
    :line-number="false"
    :network-info="eventAlertInfo.networkInfo"
    title="Message"
  />
  <notification-status-window
    v-model:is-show="notificationStatusWindowInfo.isShow"
    :available-status="notificationStatusWindowInfo.availableStatus"
    :initial-selected-type="notificationStatusWindowInfo.selectedType"
    :result-id="notificationStatusWindowInfo.resultId"
    :time-period-info="notificationStatusWindowTimePeriodInfo"
  />
</template>

<style lang="scss">
.alert-detail-history {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;

  .alert-history-toolbar {
    display: flex;
    justify-content: flex-end;
    margin: 0 10px;
  }

  .time-period-refresh {
    margin: 0 10px;
  }

  .alert-history-chart {
    height: 166px;
    margin: 12px 10px 24px;
    border: 1px solid var(--alert-history-chart-border);

    &__title {
      height: 32px;
      padding: 0 12px;
      font-size: 14px;
      font-weight: 700;
      line-height: 32px;
    }

    &__content {
      height: calc(100% - 32px);
      padding: 12px;
    }

    .ev-chart {
      height: calc(100% - 17px) !important;
    }

    .chart-legend {
      display: flex;
      gap: 8px;
      padding: 4px 0 0 48px;
      font-size: 10px;
      color: var(--alert-history-chart-legend-color);
    }

    .chart-legend-item {
      position: relative;
      padding-left: 10px;

      &::before {
        position: absolute;
        top: 50%;
        left: 0;
        width: 6px;
        height: 6px;
        border-radius: 6px;
        transform: translateY(-50%);
        content: '';
      }

      &.critical::before {
        background-color: v-bind('CRITICAL');
      }

      &.warning::before {
        background-color: v-bind('WARNING');
      }

      &.normal::before {
        background-color: v-bind('NORMAL');
      }
    }
  }

  .alert-history-tab {
    flex: 1;
    min-height: 0;

    .ev-tabs-body {
      padding: 0;
    }

    .ev-tab-panel {
      display: flex;
      position: relative;
      flex-direction: column;
      padding: 16px 10px;
    }
  }
  .list-header {
    height: 20px;
    padding-bottom: 8px;

    &__wrapper-left {
      gap: 8px;
    }
  }

  .alert-history {
    .base-grid__content {
      display: flex;
      flex-direction: column;
    }

    .grid-wrapper {
      min-height: 0;
    }
  }

  .tree-grid-toolbar {
    .grid-toolbar {
      gap: 8px;
      width: calc(100% - 32px);
    }
    .with-grid-setting {
      flex-shrink: 0;
    }
  }
}
.rule-helps {
  padding: 6px 4px;
  .rule-help {
    padding: 12px 0;
    &:first-child {
      padding-top: 0;
    }
    &:last-child {
      padding-bottom: 0;
    }
    &:nth-child(n + 2) {
      border-top: 1px solid var(--alert-history-tooltip-splitter-color);
    }
    &__header {
      margin-bottom: 8px;
      font-weight: 700;
    }
    &__content {
      display: flex;
      flex-direction: column;
      gap: 8px;
      white-space: pre;
    }
  }
}
.ev-message-box:has(.excel-oom-msg) {
  width: 560px !important;

  .excel-oom-msg {
    white-space: pre-wrap;
  }
}
</style>

<style scoped lang="scss">
.empty-table-body {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px;
  color: var(--empty-color);
}
:deep(.alert-history) {
  td.is-empty {
    display: none;
  }
}
</style>
